import { useMemo } from 'react'
import { useCacheSlice } from '../hooks'

export type ReportingEntry = {
  id: number
  prof_id: string
  prof_name: string
  date: string
  start_time: string | null
  end_time: string | null
  units: number
  description: string
  matter_id: string | null
  matter_number: string | null
  matter_description: string | null
  client: string | null
  application: string | null
  task: string | null
  activity: string | null
  document_name: string | null
  exported: boolean
  approved: boolean
  needs_improvement: boolean
  attorney_feedback: string | null
  units_seconds: number | null
  original_description: string | null
  original_units: number | null
  original_matter: string | null
  original_task: string | null
  original_activity: string | null
  status?: ReportingEntryStatus
}

export type ReportingEntryStatus = 'tracked' | 'exported' | 'deleted'

export type ReportingEntriesData = {
  entries: ReportingEntry[]
  userProfId: string
}

export function useReportingEntriesCache(profId?: string | null) {
  const { data, isLoading, lastFetchedAt } = useCacheSlice('reporting_entries')
  const { entries: allEntries, userProfId } = data as ReportingEntriesData

  const isInitialLoading = isLoading && lastFetchedAt === 0

  const entries = useMemo(() => {
    const out: Record<ReportingEntryStatus, ReportingEntry[]> = {
      tracked: [], exported: [], deleted: [],
    }
    if (!profId) return out
    for (const e of allEntries) {
      if (e.prof_id !== profId) continue
      out[e.status ?? (e.exported ? 'exported' : 'tracked')].push(e)
    }
    return out
  }, [allEntries, profId])

  return { ...entries, allEntries, userProfId, isLoading: isInitialLoading }
}
